"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RegionLevel = exports.REGION_CODE_LENGTH = exports.REGION_DETAIL_MAX_LENGTH = exports.REGION_DATA_VERSION = exports.REGION_DATA_SOURCE = exports.REGION_DATA_COUNTS = void 0;
exports.provinceCodeOf = provinceCodeOf;
exports.cityCodeOf = cityCodeOf;
exports.regionLevelOf = regionLevelOf;
exports.listProvinces = listProvinces;
exports.listCities = listCities;
exports.listDistricts = listDistricts;
exports.hasDistricts = hasDistricts;
exports.regionName = regionName;
exports.emptyRegionValue = emptyRegionValue;
exports.normalizeRegionValue = normalizeRegionValue;
exports.isRegionComplete = isRegionComplete;
exports.isRegionDetailFilled = isRegionDetailFilled;
exports.regionPathOf = regionPathOf;
exports.regionPathText = regionPathText;
exports.formatRegionText = formatRegionText;
exports.regionCascaderOptions = regionCascaderOptions;
/**
 * 行政区划（省 / 市 / 区三级）—— 跨端单一来源
 * -----------------------------------------------------------------------------
 * 为什么放 `shared`：邮寄地址要求**结构化**存省 / 市 / 区，这是「客服码按报名区域匹配」
 * （C-10）的前提；而**学员端渲染选择器、后台配置客服码区域、服务端校验**三处必须用
 * **同一份**码表。各端各存一份行政区划数据 = 迟早出现「前端能选、后端不认」。
 *
 * 数据本体在 `./data/regions.ts`（生成文件，源与许可证见其头注）。
 *
 * 口径（**D-05 裁决**，2026-10-16）：承载形态 = **3 个级联选择 + 1 个详细地址文本**，
 * 封装为**一个** `region` 字段类型（见 `form-schema.ts` 的 `FieldType.REGION`）。
 *
 * ⚠️ **不设区的市**（东莞 / 中山 / 嘉峪关 / 儋州）**只到市级** —— 第三级为空是合法状态，
 * 判「填完没填完」时**不能**一刀切要求三级齐全。
 *
 * ⚠️ 本模块是**纯逻辑 + 静态数据**，不依赖任何 UI 库：渲染由两端的组件各自完成
 * （H5 用 antd-mobile `CascadePicker`，后台用 antd `Cascader`）。
 */
const regions_js_1 = require("./data/regions.js");
var regions_js_2 = require("./data/regions.js");
Object.defineProperty(exports, "REGION_DATA_COUNTS", { enumerable: true, get: function () { return regions_js_2.REGION_DATA_COUNTS; } });
Object.defineProperty(exports, "REGION_DATA_SOURCE", { enumerable: true, get: function () { return regions_js_2.REGION_DATA_SOURCE; } });
Object.defineProperty(exports, "REGION_DATA_VERSION", { enumerable: true, get: function () { return regions_js_2.REGION_DATA_VERSION; } });
/** 详细地址长度上限（与 `FormFieldValidation.maxLength` 的默认口径一致） */
exports.REGION_DETAIL_MAX_LENGTH = 120;
/** 行政区划码长度（一律 6 位） */
exports.REGION_CODE_LENGTH = 6;
/** 层级 */
exports.RegionLevel = {
    PROVINCE: 1,
    CITY: 2,
    DISTRICT: 3,
};
/** 把 `110000|北京市,120000|天津市` 解析成节点数组（模块加载时一次性完成） */
function parse(raw) {
    return raw.split(',').map((item) => {
        const sep = item.indexOf('|');
        return { code: item.slice(0, sep), name: item.slice(sep + 1) };
    });
}
const ALL_PROVINCES = parse(regions_js_1.PROVINCE_DATA);
const ALL_CITIES = parse(regions_js_1.CITY_DATA);
const ALL_DISTRICTS = parse(regions_js_1.DISTRICT_DATA);
/** 码 → 名称（三级合一，供回显用） */
const NAME_BY_CODE = new Map();
for (const node of [...ALL_PROVINCES, ...ALL_CITIES, ...ALL_DISTRICTS]) {
    NAME_BY_CODE.set(node.code, node.name);
}
const CITIES_BY_PROVINCE = new Map();
const DISTRICTS_BY_CITY = new Map();
for (const city of ALL_CITIES) {
    const key = provinceCodeOf(city.code);
    const bucket = CITIES_BY_PROVINCE.get(key);
    if (bucket)
        bucket.push(city);
    else
        CITIES_BY_PROVINCE.set(key, [city]);
}
for (const district of ALL_DISTRICTS) {
    const key = cityCodeOf(district.code);
    const bucket = DISTRICTS_BY_CITY.get(key);
    if (bucket)
        bucket.push(district);
    else
        DISTRICTS_BY_CITY.set(key, [district]);
}
/** 省级码：前 2 位 + `0000`（`110105` → `110000`）；非 6 位输入返回空串 */
function provinceCodeOf(code) {
    if (code.length !== exports.REGION_CODE_LENGTH)
        return '';
    return `${code.slice(0, 2)}0000`;
}
/** 市级码：前 4 位 + `00`（`110105` → `110100`）；非 6 位输入返回空串 */
function cityCodeOf(code) {
    if (code.length !== exports.REGION_CODE_LENGTH)
        return '';
    return `${code.slice(0, 4)}00`;
}
/** 码的层级；`6` 位以外的输入一律返回 `null`（不接受 9 位统计用区划码） */
function regionLevelOf(code) {
    if (!/^\d{6}$/.test(code))
        return null;
    if (code.endsWith('0000'))
        return exports.RegionLevel.PROVINCE;
    if (code.endsWith('00'))
        return exports.RegionLevel.CITY;
    return exports.RegionLevel.DISTRICT;
}
function listProvinces() {
    return ALL_PROVINCES;
}
function listCities(provinceCode) {
    return CITIES_BY_PROVINCE.get(provinceCode) ?? [];
}
function listDistricts(cityCode) {
    return DISTRICTS_BY_CITY.get(cityCode) ?? [];
}
/** 该市是否有区县（**不设区的市**返回 `false`，此时市即最细粒度） */
function hasDistricts(cityCode) {
    return listDistricts(cityCode).length > 0;
}
/** 码 → 名称；未知码返回 `null`（调用方自行决定回退文案） */
function regionName(code) {
    if (!code)
        return null;
    return NAME_BY_CODE.get(code) ?? null;
}
/** 空值（未选择） */
function emptyRegionValue() {
    return { provinceCode: '', cityCode: '', districtCode: '', detail: '' };
}
/**
 * 把任意输入规整成 `RegionValue`。
 *
 * 只做**形状与码表**校验（不含业务必填规则，那是 `form-validator` 的事）：
 *   1. 码必须**存在于码表内**（未知码丢弃，避免脏值进表单 / 数据库）；
 *   2. 码必须**落在正确的层级**（`110105` 是区码，塞进省槽位同样丢弃 ——
 *      否则 `{provinceCode:'110105'}` 这类错位值会被当合法值放行）；
 *   3. 父级不成立时子级一并作废（避免出现「有区码却没有省码」的错配）。
 */
function normalizeRegionValue(input) {
    if (typeof input !== 'object' || input === null)
        return emptyRegionValue();
    const raw = input;
    const pick = (value, level) => typeof value === 'string' && regionLevelOf(value) === level && NAME_BY_CODE.has(value)
        ? value
        : '';
    const provinceCode = pick(raw.provinceCode, exports.RegionLevel.PROVINCE);
    const cityCode = pick(raw.cityCode, exports.RegionLevel.CITY);
    const districtCode = pick(raw.districtCode, exports.RegionLevel.DISTRICT);
    /**
     * 子级必须挂在**已生效的父级**下 —— 注意这里用的是收敛后的 `safeCityCode`，
     * 不是原始 `cityCode`。
     *
     * 曾经用了原始值：`{provinceCode:'110000', cityCode:'440300', districtCode:'440304'}`
     * （省=北京、市=深圳、区=深圳福田）会把市丢掉、把区留下，拼成
     * 「北京市 / 福田区」这种根本不存在的地址，一路印到详情页与客服码匹配上。
     */
    const safeCityCode = provinceCode && cityCode && provinceCodeOf(cityCode) === provinceCode ? cityCode : '';
    const safeDistrictCode = safeCityCode && districtCode && cityCodeOf(districtCode) === safeCityCode ? districtCode : '';
    return {
        provinceCode,
        cityCode: safeCityCode,
        districtCode: safeDistrictCode,
        detail: typeof raw.detail === 'string' ? raw.detail : '',
    };
}
/**
 * 「填完了没有」。
 *
 * ⚠️ **不设区的市只要求到市级** —— 东莞 / 中山 / 嘉峪关 / 儋州 无区县，
 * 一刀切要求三级齐全会让这些地方的学员**永远填不完**。
 */
function isRegionComplete(input) {
    const value = normalizeRegionValue(input);
    if (!value.provinceCode || !value.cityCode)
        return false;
    return hasDistricts(value.cityCode) ? Boolean(value.districtCode) : true;
}
/** 详细地址是否填写（与三级码分开判定：允许「只选到市级」但详细地址必填） */
function isRegionDetailFilled(input) {
    const value = normalizeRegionValue(input);
    return value.detail.trim().length > 0;
}
/** 已选的层级列表（用于回显成「省 / 市 / 区」三段） */
function regionPathOf(input) {
    const value = normalizeRegionValue(input);
    const path = [];
    for (const code of [value.provinceCode, value.cityCode, value.districtCode]) {
        const name = regionName(code);
        if (code && name)
            path.push({ code, name });
    }
    return path;
}
/** 已选层级的名称文本：`北京市 / 市辖区 / 朝阳区`（**不含**详细地址；两端选择器触发钮回显用） */
function regionPathText(input, separator = ' / ') {
    return regionPathOf(input)
        .map((node) => node.name)
        .join(separator);
}
/** 文本化：`北京市 / 市辖区 / 朝阳区 建国路 88 号`（后台表格与只读回显用） */
function formatRegionText(input, separator = ' / ') {
    const value = normalizeRegionValue(input);
    const head = regionPathText(value, separator);
    const detail = value.detail.trim();
    if (head && detail)
        return `${head} ${detail}`;
    return head || detail;
}
/**
 * 级联选择器的选项树。
 *
 * 懒构建 + 缓存：31 × 342 × 2974 ≈ 3300 个节点，只有真正渲染选择器时才付这份内存。
 * 不设区的市的第三级自然为空数组 —— 调用方按「无 children」当叶子处理。
 */
let cascaderOptions = null;
function regionCascaderOptions() {
    if (cascaderOptions)
        return cascaderOptions;
    cascaderOptions = ALL_PROVINCES.map((province) => ({
        value: province.code,
        label: province.name,
        children: listCities(province.code).map((city) => {
            const districts = listDistricts(city.code);
            return districts.length > 0
                ? {
                    value: city.code,
                    label: city.name,
                    children: districts.map((district) => ({
                        value: district.code,
                        label: district.name,
                    })),
                }
                : { value: city.code, label: city.name };
        }),
    }));
    return cascaderOptions;
}
//# sourceMappingURL=region.js.map